import {useDocumentHeight, useWindowHeight} from '@hooks';
import {getBaseRemSize} from '@utils/getBaseRemSize';
import {interpolate} from '@utils/interpolate';
import {useCallback, useEffect, useRef, useState} from 'react';

const MIN_FREE_SPACE_AFTER_LAST_FITTING_SECTION = 100;

export const useScrollToSection = (sectionIds: string[]) => {
    const [activeSectionIndex, setActiveSectionIndex] = useState(0);
    const windowHeight = useWindowHeight();
    const documentHeight = useDocumentHeight();

    const skipScrollEvents = useRef(false);

    const scrollToSection = useCallback(
        (sectionId: string) => {
            setActiveSectionIndex(sectionIds.findIndex(id => id === sectionId));
            const element = document.getElementById(sectionId);
            if (element) {
                skipScrollEvents.current = true;
                element.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                });
            }
        },
        [sectionIds],
    );

    useEffect(() => {
        if (!windowHeight || !documentHeight) {
            return;
        }

        const sections: {el: Element; top: number}[] = [];

        sectionIds.forEach(id => {
            const element = document.getElementById(id);
            if (element) {
                const scrollMarginTop =
                    getComputedStyle(element).scrollMarginTop;
                const rems =
                    scrollMarginTop.includes('rem') &&
                    parseFloat(scrollMarginTop.replace('rem', '')) *
                        getBaseRemSize();
                const px =
                    scrollMarginTop.includes('px') &&
                    parseFloat(scrollMarginTop.replace('px', ''));

                const top =
                    element.getBoundingClientRect().top -
                    document.body.getBoundingClientRect().top -
                    (rems || px || 0);

                sections.push({
                    el: element,
                    top: top,
                });
            }
        });

        if (!sections.length) {
            // TODO - something to allow sections appearing/disappearing dynamically after the mount of the page.
            return;
        }

        let lastTopThatFits = 0;

        const interpolatedSections = sections.map(element => {
            if (
                element.top <
                documentHeight -
                    windowHeight -
                    MIN_FREE_SPACE_AFTER_LAST_FITTING_SECTION
            ) {
                lastTopThatFits = element.top;
                return element;
            }

            if (lastTopThatFits === 0) {
                return element;
            }

            const lastTop = sections.at(-1)!.top;

            const newTop = interpolate(element.top, {
                from: [lastTopThatFits, lastTop],
                to: [lastTopThatFits, documentHeight - windowHeight - 1],
            });

            return {
                ...element,
                top: newTop,
            };
        });

        const reenableScrollEvents = () => {
            skipScrollEvents.current = false;
        };

        const calculateActive = () => {
            if (skipScrollEvents.current) {
                return;
            }

            const scrollTop = window.scrollY;

            const activeSectionIdx = interpolatedSections.findLastIndex(
                element => element.top <= scrollTop,
            );

            if (activeSectionIdx !== -1) {
                setActiveSectionIndex(activeSectionIdx);
            }
        };

        calculateActive();

        window.addEventListener('wheel', reenableScrollEvents);
        window.addEventListener('mousedown', reenableScrollEvents);
        window.addEventListener('touchmove', reenableScrollEvents);
        window.addEventListener('keydown', reenableScrollEvents);
        window.addEventListener('scroll', calculateActive);

        return () => {
            window.removeEventListener('wheel', reenableScrollEvents);
            window.removeEventListener('mousedown', reenableScrollEvents);
            window.removeEventListener('touchmove', reenableScrollEvents);
            window.removeEventListener('keydown', reenableScrollEvents);
            window.removeEventListener('scroll', calculateActive);
        };
    }, [sectionIds, documentHeight, windowHeight]);

    return {
        activeSectionIndex,
        scrollToSection,
    };
};
